import React, { useState, useEffect, useRef, useMemo } from 'react';
import { usersAPI, locationsAPI, warehousesAPI } from '@/lib/api';
import { Plus, Pencil, Trash2, User, Camera, X, Upload, Warehouse, AlertTriangle, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LugaresRecuentoField } from './LugaresRecuentoField';
import { recuentosAPI } from '@/lib/recuentos';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminUsersPage = () => {
  const [users, setUsers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [lugaresStock, setLugaresStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [selectedUserForImage, setSelectedUserForImage] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    username: '',
    password: '',
    role: 'local',
    location_id: '',
    warehouse_id: '',
    order_type: 'cocina',
    lugares_recuento: [],
    is_active: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [usersRes, locationsRes, warehousesRes, lugaresRes] = await Promise.all([
        usersAPI.getAll(),
        locationsAPI.getAll(true),
        warehousesAPI.getActive(),
        recuentosAPI.lugares()
      ]);
      setLugaresStock(lugaresRes.data);
      setUsers(usersRes.data);
      setLocations(locationsRes.data);
      setWarehouses(warehousesRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  // Calcular usuarios problemáticos (tipo local/inventarista sin location_id)
  const usersWithoutLocation = useMemo(() => {
    return users.filter(u => 
      (u.role === 'local' || u.role === 'inventarista') && !u.location_id
    );
  }, [users]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...formData };
      if (formData.role === 'admin') {
        submitData.location_id = null;
      }
      // Convertir cadenas vacías a null
      if (!submitData.location_id) submitData.location_id = null;
      if (!submitData.warehouse_id) submitData.warehouse_id = null;
      
      if (editingUser && !submitData.password) {
        delete submitData.password;
      }

      if (editingUser) {
        await usersAPI.update(editingUser.id, submitData);
        toast.success('Usuario actualizado');
      } else {
        await usersAPI.create(submitData);
        toast.success('Usuario creado');
      }
      setDialogOpen(false);
      loadData();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar usuario');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este usuario?')) {
      try {
        await usersAPI.delete(id);
        toast.success('Usuario eliminado');
        loadData();
      } catch (error) {
        toast.error('Error al eliminar usuario');
      }
    }
  };

  const openCreateDialog = () => {
    resetForm();
    setEditingUser(null);
    setDialogOpen(true);
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      username: user.username,
      password: '',
      role: user.role,
      location_id: user.location_id || '',
      warehouse_id: user.warehouse_id || '',
      order_type: user.order_type || 'cocina',
      lugares_recuento: user.lugares_recuento || [],
      is_active: user.is_active
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      username: '',
      password: '',
      role: 'local',
      location_id: '',
      warehouse_id: '',
      order_type: 'cocina',
      lugares_recuento: [],
      is_active: true
    });
  };

  const getLocationName = (locationId) => {
    const location = locations.find(l => l.id === locationId);
    return location ? location.name : '-';
  };

  const getWarehouseName = (warehouseId) => {
    const warehouse = warehouses.find(w => w.id === warehouseId);
    return warehouse ? warehouse.name : '-';
  };

  const openImageDialog = (user) => {
    setSelectedUserForImage(user);
    setImageDialogOpen(true);
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !selectedUserForImage) return;

    // Validar tipo de archivo
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Tipo de archivo no permitido. Use JPG, PNG, WebP o GIF');
      return;
    }

    // Validar tamaño (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('La imagen no debe superar 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      const response = await usersAPI.uploadProfileImage(selectedUserForImage.id, file);
      setUsers(prev => prev.map(u => u.id === selectedUserForImage.id ? response.data : u));
      setSelectedUserForImage(response.data);
      toast.success('Imagen actualizada correctamente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir la imagen');
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteImage = async () => {
    if (!selectedUserForImage) return;
    
    setUploadingImage(true);
    try {
      const response = await usersAPI.deleteProfileImage(selectedUserForImage.id);
      setUsers(prev => prev.map(u => u.id === selectedUserForImage.id ? response.data : u));
      setSelectedUserForImage(response.data);
      toast.success('Imagen eliminada');
    } catch (error) {
      toast.error('Error al eliminar la imagen');
    } finally {
      setUploadingImage(false);
    }
  };

  const getProfileImageUrl = (user) => {
    if (!user?.profile_image) return null;
    return `${API_URL}${user.profile_image}`;
  };

  return (
    <div className="space-y-6" data-testid="admin-users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-slate-900">Usuarios</h1>
          <p className="text-muted-foreground mt-1">Gestiona los usuarios del sistema</p>
        </div>
        <Button onClick={openCreateDialog} data-testid="create-user-button">
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Usuario
        </Button>
      </div>

      {/* Alerta de usuarios sin local asignado */}
      {usersWithoutLocation.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5" />
            <div>
              <p className="text-amber-800 font-semibold">
                ⚠️ {usersWithoutLocation.length} usuario(s) sin local asignado
              </p>
              <p className="text-amber-700 text-sm mt-1">
                Estos usuarios (tipo "local" o "inventarista") no podrán hacer pedidos o inventarios correctamente. 
                Haz clic en "Editar" para asignarles un local:
              </p>
              <div className="mt-2 flex flex-wrap gap-2">
                {usersWithoutLocation.map(u => (
                  <span key={u.id} className="inline-flex items-center px-2 py-1 bg-amber-100 text-amber-700 rounded text-sm">
                    <User className="w-3 h-3 mr-1" />
                    {u.username} ({u.role})
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Foto</th>
                <th className="px-4 py-3 text-left font-semibold">Usuario</th>
                <th className="px-4 py-3 text-left font-semibold">Nombre</th>
                <th className="px-4 py-3 text-left font-semibold">Email</th>
                <th className="px-4 py-3 text-left font-semibold">Rol</th>
                <th className="px-4 py-3 text-left font-semibold">Tipo Pedido</th>
                <th className="px-4 py-3 text-left font-semibold">Local</th>
                <th className="px-4 py-3 text-left font-semibold">Bodega</th>
                <th className="px-4 py-3 text-left font-semibold">Recuentos</th>
                <th className="px-4 py-3 text-center font-semibold">Estado</th>
                <th className="px-4 py-3 text-center font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} className="border-b hover:bg-slate-50/50 transition-colors">
                  <td className="px-4 py-3">
                    <div 
                      className="relative w-12 h-12 rounded-full overflow-hidden bg-slate-100 cursor-pointer group"
                      onClick={() => openImageDialog(user)}
                    >
                      {user.profile_image ? (
                        <img 
                          src={getProfileImageUrl(user)} 
                          alt={user.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-200 to-slate-300">
                          <User className="w-6 h-6 text-slate-500" />
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Camera className="w-4 h-4 text-white" />
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-sm">{user.username}</span>
                  </td>
                  <td className="px-4 py-3 font-semibold text-slate-900">{user.name}</td>
                  <td className="px-4 py-3 text-sm text-slate-600">{user.email}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                      user.role === 'admin' ? 'bg-purple-100 text-purple-700' : 
                      user.role === 'bodega' ? 'bg-green-100 text-green-700' :
                      user.role === 'logistica' ? 'bg-tupu-orange-100 text-tupu-orange-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {user.role === 'admin' ? 'Administrador' : 
                       user.role === 'bodega' ? 'Bodega' : 
                       user.role === 'logistica' ? 'Logística' :
                       user.role === 'inventarista' ? 'Inventarista' : 'Local'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {(user.role === 'local' || user.role === 'inventarista') ? (
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                        user.order_type === 'barra' 
                          ? 'bg-amber-100 text-amber-700' 
                          : 'bg-cyan-100 text-cyan-700'
                      }`}>
                        {user.order_type === 'barra' ? 'Barra' : 'Cocina'}
                      </span>
                    ) : (
                      <span className="text-slate-400 text-sm">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-600">{getLocationName(user.location_id)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <Warehouse className="w-3 h-3 text-slate-400" />
                      <span className="text-sm text-slate-600">{getWarehouseName(user.warehouse_id)}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600" data-testid={`user-lugares-${user.username}`}>
                    {(user.lugares_recuento || []).length
                      ? user.lugares_recuento.map((c) => {
                          const l = lugaresStock.find((x) => x.codigo === c);
                          return l ? `${l.local} ${l.nombre_bodega_justo.replace('Bodega ', '')}` : c;
                        }).join(', ')
                      : <span className="text-slate-400">-</span>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                      user.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700'
                    }`}>
                      {user.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openImageDialog(user)}
                        data-testid={`user-photo-btn-${user.username}`}
                        title="Cambiar foto"
                      >
                        <Camera className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEditDialog(user)}
                        data-testid={`user-edit-btn-${user.username}`}
                        title="Editar usuario"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(user.id)}
                        data-testid={`user-delete-btn-${user.username}`}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        title="Eliminar usuario"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div>
                <Label htmlFor="name">Nombre *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="username">Usuario *</Label>
                <Input
                  id="username"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>
              <div>
                <Label htmlFor="password">Contraseña {editingUser ? '(dejar vacío para mantener)' : '*'}</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingUser}
                />
              </div>
              <div>
                <Label htmlFor="role">Rol *</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value, location_id: (value === 'admin' || value === 'bodega' || value === 'logistica') ? '' : formData.location_id })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Administrador</SelectItem>
                    <SelectItem value="bodega">Bodega</SelectItem>
                    <SelectItem value="logistica">Logística</SelectItem>
                    <SelectItem value="inventarista">Inventarista</SelectItem>
                    <SelectItem value="local">Usuario Local</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {(formData.role === 'local' || formData.role === 'inventarista') && (
                <div>
                  <Label htmlFor="location_id">Local *</Label>
                  <Select value={formData.location_id} onValueChange={(value) => setFormData({ ...formData, location_id: value })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar local" />
                    </SelectTrigger>
                    <SelectContent>
                      {locations.map(location => (
                        <SelectItem key={location.id} value={location.id}>{location.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              {/* Selector de Tipo de Pedido - solo para usuarios de tipo local */}
              {formData.role === 'local' && (
                <div>
                  <Label htmlFor="order_type">Tipo de Pedido</Label>
                  <Select 
                    value={formData.order_type} 
                    onValueChange={(value) => setFormData({ ...formData, order_type: value })}
                  >
                    <SelectTrigger data-testid="user-order-type-select">
                      <SelectValue placeholder="Seleccionar tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cocina">Cocina (Encargado)</SelectItem>
                      <SelectItem value="barra">Barra</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    Los pedidos de "Barra" y "Cocina" son independientes aunque compartan el mismo local
                  </p>
                </div>
              )}
              
              {/* Selector de Bodega - visible para todos los roles que lo necesiten */}
              <div>
                <Label htmlFor="warehouse_id">Bodega Asignada</Label>
                <Select 
                  value={formData.warehouse_id} 
                  onValueChange={(value) => setFormData({ ...formData, warehouse_id: value === 'none' ? '' : value })}
                >
                  <SelectTrigger data-testid="user-warehouse-select">
                    <SelectValue placeholder="Seleccionar bodega" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin asignar</SelectItem>
                    {warehouses.map(warehouse => (
                      <SelectItem key={warehouse.id} value={warehouse.id}>{warehouse.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  {formData.role === 'local' 
                    ? 'Al hacer pedidos, el inventario se descontará de esta bodega'
                    : formData.role === 'bodega'
                    ? 'El usuario verá los pedidos de los locales asignados a esta bodega'
                    : 'Bodega asociada al usuario'
                  }
                </p>
              </div>
              {formData.role !== 'admin' && (
                <LugaresRecuentoField
                  lugares={lugaresStock}
                  value={formData.lugares_recuento}
                  onChange={(v) => setFormData({ ...formData, lugares_recuento: v })}
                />
              )}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit">
                {editingUser ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog para editar imagen de perfil */}
      <Dialog open={imageDialogOpen} onOpenChange={setImageDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Imagen de Perfil</DialogTitle>
          </DialogHeader>
          
          {selectedUserForImage && (
            <div className="flex flex-col items-center py-6">
              {/* Preview de imagen */}
              <div className="relative w-40 h-40 rounded-full overflow-hidden bg-slate-100 mb-6 border-4 border-slate-200">
                {selectedUserForImage.profile_image ? (
                  <img 
                    src={getProfileImageUrl(selectedUserForImage)} 
                    alt={selectedUserForImage.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-200 to-slate-300">
                    <User className="w-16 h-16 text-slate-400" />
                  </div>
                )}
              </div>

              <p className="text-lg font-semibold text-slate-900 mb-1">{selectedUserForImage.name}</p>
              <p className="text-sm text-slate-500 mb-6">@{selectedUserForImage.username}</p>

              {/* Input de archivo oculto */}
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handleImageUpload}
                className="hidden"
              />

              {/* Botones de acción */}
              <div className="flex gap-3 w-full">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingImage}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploadingImage ? 'Subiendo...' : 'Subir imagen'}
                </Button>
                
                {selectedUserForImage.profile_image && (
                  <Button
                    variant="outline"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={handleDeleteImage}
                    disabled={uploadingImage}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>

              <p className="text-xs text-slate-400 mt-4 text-center">
                JPG, PNG, WebP o GIF. Máximo 5MB.<br/>
                La imagen se redimensionará a 500x500px
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsersPage;